import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";

/**
 * Banner discreto en el Panel que invita a activar la recarga automática de
 * créditos IA cuando el saldo está bajo (≥80% consumido) y la función está
 * desactivada. Descartable por sesión (sessionStorage por restaurante).
 */
export default function AutoRefillNudge({ restaurantId }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!restaurantId) return;
    if (sessionStorage.getItem(`mg-arnudge-${restaurantId}`)) return;
    let cancelled = false;
    (async () => {
      try {
        const [subRes, arRes] = await Promise.all([
          api.get(`/subscription/my/${restaurantId}`).catch(() => null),
          api.getAutoRefill(restaurantId).catch(() => null),
        ]);
        if (cancelled) return;
        const sub = subRes?.subscription;
        if (!sub || !arRes) return;
        const pctUsed = sub.pct_used || 0;
        const active = ["active", "trial"].includes(sub.status);
        if (active && pctUsed >= 80 && !arRes.enabled) {
          setPct(Math.round(pctUsed));
          setShow(true);
        }
      } catch { /* silent — nudge is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  if (!show) return null;

  const dismiss = () => {
    sessionStorage.setItem(`mg-arnudge-${restaurantId}`, "1");
    setShow(false);
  };

  return (
    <div
      className="relative flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4"
      data-testid="autorefill-nudge"
    >
      <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <RefreshCw className="w-4 h-4 text-amber-500" />
      </div>
      <div className="flex-1 min-w-0 pr-6">
        <p className="text-sm font-semibold">Tu saldo de créditos IA está bajo ({pct}% consumido)</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Activa la recarga automática y nunca te quedarás sin IA: cobramos tu tarjeta y añadimos
          créditos automáticamente cuando el saldo baje.
        </p>
        <Button
          size="sm"
          className="mt-3 h-8 rounded-full gap-1.5 text-xs"
          onClick={() => navigate("/app/subscription?tab=autorefill")}
          data-testid="autorefill-nudge-cta"
        >
          Activar recarga automática <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Descartar aviso"
        data-testid="autorefill-nudge-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
